/* La Sura - "Find the right cake" routing quiz. A client-side router: the
   screens are Liquid, answers build a small state, and the result is links
   computed from it. No backend, nothing stored. State-dependent branching
   lives here; the result is built with DOM methods, never innerHTML. */
class LasuraQuiz extends HTMLElement {
  connectedCallback() {
    try {
      this.copy = JSON.parse(this.querySelector('script[data-quiz-copy]')?.textContent || '{}');
    } catch (error) {
      this.copy = {};
    }
    this.screens = Array.from(this.querySelectorAll('[data-quiz-screen]'));
    if (!this.screens.length) return;
    this.state = {};
    this.history = [];
    this.backBtn = this.querySelector('[data-quiz-back]');
    if (this.backBtn) this.backBtn.addEventListener('click', () => this.back());

    this.querySelectorAll('[data-quiz-answer]').forEach((btn) => {
      btn.addEventListener('click', () => this.answer(btn));
    });
    this.querySelectorAll('[data-quiz-continue]').forEach((btn) => {
      btn.addEventListener('click', () => this.continueMulti(btn));
    });
    this.querySelectorAll('[data-quiz-restart]').forEach((btn) => {
      btn.addEventListener('click', () => this.restart());
    });

    this.show('q_occasion', false);
    this.track('quiz_start', {});
  }

  screen(id) {
    return this.screens.find((s) => s.dataset.quizScreen === id);
  }

  currentId() {
    const visible = this.screens.find((s) => !s.hidden);
    return visible ? visible.dataset.quizScreen : null;
  }

  answer(btn) {
    const key = btn.dataset.key;
    /* Changing what they're looking for starts a new path, so the old path's answers go
       (otherwise Back then a different choice kept the old size and routed on it). */
    if (key === 'type') {
      delete this.state.serves;
      delete this.state.crowd_size;
      delete this.state.crowd_kind;
    }
    if (key) this.state[key] = btn.dataset.value;
    this.track('quiz_step', { question: this.currentId(), answer: btn.dataset.value || '' });
    this.goto(btn.dataset.goto);
  }

  continueMulti(btn) {
    const boxes = btn.closest('[data-quiz-screen]').querySelectorAll('input[type="checkbox"]:checked');
    this.state.dietary = Array.from(boxes).map((b) => b.value).filter((v) => v !== 'None');
    this.track('quiz_step', { question: this.currentId(), answer: this.state.dietary.join('|') || 'None' });
    this.goto(btn.dataset.goto);
  }

  goto(next) {
    /* State-dependent redirects the markup cannot know. */
    if (next === 'q_fulfil' && this.state.occasion === 'Wedding') {
      next = 'result';
    }
    if (next === 'q_fulfil') {
      const postable = ['Cupcakes', 'Brownie boxes', 'A mix of treats'].includes(this.state.crowd_kind);
      const postedBtn = this.querySelector('[data-quiz-posted]');
      if (postedBtn) postedBtn.hidden = !postable;
    }
    if (next === 'result') {
      this.renderResult();
    }
    this.history.push(this.currentId());
    this.show(next);
  }

  back() {
    const prev = this.history.pop();
    if (prev) this.show(prev);
  }

  restart() {
    this.state = {};
    this.history = [];
    this.querySelectorAll('input[type="checkbox"]').forEach((b) => { b.checked = false; });
    this.show('q_occasion');
  }

  show(id, focus = true) {
    this.screens.forEach((s) => { s.hidden = s.dataset.quizScreen !== id; });
    if (this.backBtn) this.backBtn.hidden = this.history.length === 0 || id === 'result';
    if (focus) {
      const h = this.screen(id).querySelector('[data-quiz-heading]');
      if (h) {
        h.setAttribute('tabindex', '-1');
        h.focus();
      }
    }
  }

  /* The routing table. Returns { primary, secondary[], notes[] }. Every label,
     url and note below is a hardcoded constant; nothing user-typed enters. */
  route() {
    const s = this.state;
    const dietary = s.dietary || [];
    const wantsDietary = dietary.includes('Vegan') || dietary.includes('Made without gluten');
    const enquiry = (extra) => {
      const p = new URLSearchParams();
      if (s.occasion) p.set('occasion', s.occasion);
      if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v));
      return '/pages/bespoke-enquiry?' + p.toString();
    };
    const L = (label, url) => ({ label: label, url: url });
    /* Words come from the section's settings (Results); every one has a default there. */
    const C = (key) => (this.copy && this.copy[key]) || 'Browse our cakes';
    /* A note the merchant clears is simply not shown. */
    const N = (key) => { if (this.copy && this.copy[key]) notes.push(this.copy[key]); };

    let primary = L(C('browse_cakes'), '/collections/cakes');
    const secondary = [];
    const notes = [];
    let equal = false;

    if (s.occasion === 'Wedding') {
      primary = L(C('see_wedding'), '/pages/wedding');
      secondary.push(L(C('start_wedding'), enquiry()));
    } else if (s.occasion === 'Office or corporate') {
      primary = L(C('start_corporate'), enquiry());
      secondary.push(L(C('office_cupcakes'), '/collections/cupcakes'));
    } else if (s.serves === 'More than 40') {
      primary = L(C('start_tiered'), enquiry({ servings: '40' }));
      secondary.push(L(C('browse_cakes'), '/collections/cakes'));
      N('note_over_40');
    } else if (s.serves === '25 to 40') {
      /* Matt, 2026-09-25: no single cake serves 25 to 40, so both routes are offered as equals. */
      primary = L(C('big_cake_treats'), '/collections/cakes');
      secondary.push(L(C('tiered_quote'), enquiry({ servings: '25' })));
      N('note_25_40');
      equal = true;
    } else if (s.type === 'crowd' || s.type === 'both') {
      const kindMap = {
        'Cupcakes': L(C('browse_cupcakes'), '/collections/cupcakes'),
        'Brownie boxes': L(C('browse_brownies'), '/collections/brownies'),
        'A mix of treats': L(C('browse_cakes_treats'), '/collections/cakes'),
        'One big cake plus treats': L(C('browse_cakes'), '/collections/cakes')
      };
      primary = kindMap[s.crowd_kind] || primary;
      if (s.crowd_kind === 'A mix of treats' || s.crowd_kind === 'One big cake plus treats') {
        secondary.push(L(C('cupcakes'), '/collections/cupcakes'));
      }
    }

    /* Design your own: the builder, its size picked from Cake size data in Liquid. */
    const B = this.copy.builder;
    if (B && B.url && s.type === 'one' && B.sizes[s.serves] && s.timing !== 'In the next few days' && s.fulfil !== 'Posted to me') {
      const p = new URLSearchParams({ Size: B.sizes[s.serves], from: 'quiz' });
      const d = dietary.map((x) => (x === 'Nut allergy' ? 'Made without nuts' : x));
      if (d.length) p.set('Dietary', d.join('|'));
      secondary.push(L(C('design_own'), B.url + '?' + p));
    }

    if (s.fulfil === 'Posted to me') {
      secondary.unshift(primary);
      primary = L(C('see_post'), '/collections/nationwide');
    }

    if (wantsDietary) {
      if (primary.url === '/collections/cakes') {
        secondary.unshift(primary);
        primary = L(C('vegan_gf'), '/collections/vegan-gluten-free');
      } else {
        secondary.unshift(L(C('vegan_gf'), '/collections/vegan-gluten-free'));
      }
    }

    if (dietary.includes('Nut allergy')) {
      N('note_nut');
      if (!secondary.some((x) => x.url.indexOf('/pages/bespoke-enquiry') === 0)) {
        secondary.push(L(C('ask_allergies'), enquiry()));
      }
    }

    if (s.timing === 'In the next few days' && primary.url.indexOf('/pages/bespoke-enquiry') === 0) {
      secondary.unshift(primary);
      primary = L(C('ready_now'), '/collections/cakes');
      N('note_this_week');
    }

    /* In the next few days a tiered cake isn't a real option, so it stops being an equal route. */
    if (equal && s.timing === 'In the next few days') {
      equal = false;
      N('note_tiered_this_week');
    }
    /* Equal routes only while the enquiry is still the first alternative. */
    equal = equal && secondary.length > 0 && secondary[0].url.indexOf('/pages/bespoke-enquiry') === 0;
    return { primary: primary, secondary: secondary.slice(0, 2), notes: notes, equal: equal };
  }

  renderResult() {
    const r = window.lasuraQuizRefine ? window.lasuraQuizRefine(this.route(), this.state, this.copy) : this.route();
    const box = this.querySelector('[data-quiz-result]');
    if (!box) return;
    box.replaceChildren();

    r.notes.forEach((n) => {
      const p = document.createElement('p');
      p.className = 'lasura-quiz__note';
      p.textContent = n;
      box.appendChild(p);
    });

    const a = document.createElement('a');
    a.className = 'button lasura-quiz__primary';
    a.href = r.primary.url;
    a.textContent = r.primary.label;

    if (r.equal) {
      /* Two routes of equal weight, side by side. */
      const routes = document.createElement('div');
      routes.className = 'lasura-quiz__routes';
      const b = document.createElement('a');
      b.className = 'button lasura-quiz__primary';
      b.href = r.secondary[0].url;
      b.textContent = r.secondary[0].label;
      routes.append(a, b);
      box.appendChild(routes);
      r.secondary = r.secondary.slice(1);
    } else {
      box.appendChild(a);
    }

    if (r.secondary.length) {
      const alts = document.createElement('div');
      alts.className = 'lasura-quiz__alts';
      r.secondary.forEach((x) => {
        const b = document.createElement('a');
        b.className = 'button-secondary lasura-quiz__alt';
        b.href = x.url;
        b.textContent = x.label;
        alts.appendChild(b);
      });
      box.appendChild(alts);
    }

    this.track('quiz_complete', { destination: r.primary.url });
  }

  track(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, params));
  }
}

if (!customElements.get('lasura-quiz')) {
  customElements.define('lasura-quiz', LasuraQuiz);
}
